/**
 * QuickTaskItem - Ítem de tarea rápida
 *
 * Diseño simple y compacto:
 * - Checkbox para completar
 * - Texto de la tarea
 * - Botón para eliminar
 */

import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { cn } from '@/core/utils';
import { useQuickTaskStore } from '@/store';
import { feedbackSuccess } from '@/services/feedback';
import type { QuickTask } from '@/core/types';

interface QuickTaskItemProps {
  task: QuickTask;
  index: number;
}

export function QuickTaskItem({ task, index }: QuickTaskItemProps) {
  const [justCompleted, setJustCompleted] = useState(false);
  const toggleQuickTask = useQuickTaskStore((state) => state.toggleQuickTask);
  const deleteQuickTask = useQuickTaskStore((state) => state.deleteQuickTask);

  const handleToggle = useCallback(() => {
    const wasCompleted = task.isDone;
    toggleQuickTask(task.id);

    if (!wasCompleted) {
      setJustCompleted(true);
      setTimeout(() => setJustCompleted(false), 600);
      feedbackSuccess();
    }
  }, [task.id, task.isDone, toggleQuickTask]);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      deleteQuickTask(task.id);
    },
    [task.id, deleteQuickTask]
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 10, height: 0 }}
      transition={{ delay: index * 0.03 }}
      className={cn(
        'group flex items-center gap-3 px-3 py-2 rounded-lg',
        'bg-slate-800/30 border border-dashed border-slate-700/50',
        'hover:bg-slate-800/50 hover:border-slate-600/50',
        'transition-all duration-200 cursor-pointer',
        task.isDone && 'opacity-50'
      )}
      onClick={handleToggle}
      role="checkbox"
      aria-checked={task.isDone}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      }}
    >
      {/* Checkbox */}
      <motion.div
        className={cn(
          'flex-shrink-0 w-4 h-4 rounded border',
          'flex items-center justify-center',
          'transition-all duration-200',
          task.isDone
            ? 'bg-blue-500 border-blue-500'
            : 'border-slate-600 hover:border-slate-500'
        )}
        animate={justCompleted ? { scale: [1, 1.2, 1] } : {}}
        transition={{ duration: 0.3 }}
      >
        {task.isDone && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            <Check className="w-3 h-3 text-white" strokeWidth={3} />
          </motion.div>
        )}
      </motion.div>

      {/* Label */}
      <span
        className={cn(
          'flex-1 text-sm transition-colors',
          task.isDone ? 'line-through text-slate-500' : 'text-slate-300'
        )}
      >
        {task.label}
      </span>

      {/* Badge temporal */}
      <span className="text-2xs text-slate-600 font-medium">
        temporal
      </span>

      {/* Delete button */}
      <button
        onClick={handleDelete}
        className={cn(
          'p-1 rounded text-slate-600',
          'opacity-0 group-hover:opacity-100',
          'hover:text-red-400 hover:bg-red-900/20',
          'transition-all duration-200'
        )}
        title="Eliminar tarea"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
}
